import Container from "@/components/Container/Container";
import Layout from "@/components/Layout/Layout";
import { H5Css, Heading } from "@/components/Typography";
import { darkColor, mutedBlueColor } from "@/constants/colors";
import { privacyPath } from "@/constants/paths";
import { css } from "@emotion/react";
import { GetStaticProps } from "next";

const Page = () => {
  return (
    <Layout
      title="Cookie Policy"
      metaDescription="Cookie Policy"
      heroContainerCss={css`
        background: url(/images/policies/BG.svg), ${mutedBlueColor};
        background-size: cover;
        background-repeat: no-repeat;
      `}
      hero={
        <Container>
          <Heading
            level={1}
            css={css`
              color: ${darkColor};
              text-align: center;
              margin-top: 40px;
              padding-bottom: 87px;
              ${H5Css}
            `}
          >
            Cookie Policy
          </Heading>
        </Container>
      }
    >
      <Container
        css={css`
          padding: 60px 40px;
        `}
      >
        <div>
          <p style={{ textAlign: "center" }}>
            Digital Edge Sport, Inc. d/b/a Jersey Watch
          </p>
          <p style={{ textAlign: "center" }}>Cookie Policy</p>
          <p>
            In addition to the information that you provide to us, we may also
            collect information about you during your visit to our Website,
            Administrator Sites, or use of our Services. We collect this
            information using automated tools that are detailed below. These
            tools may collect information about your behavior and your computer
            system, such as your internet address (IP Address), the pages you
            have viewed, and the actions you have taken while using the Website.
            Some of the tools we use to automatically collect information about
            you may include:
          </p>
          <ol style={{ listStyleType: "lower-alpha" }}>
            <li>
              <u>Cookies</u>. A “cookie” is a small data file transmitted from a
              website to your device&apos;s hard drive. Cookies are usually
              defined in one of two ways, and we may use either (or both) of
              them:
              <br />
              <br />
              <ol>
                <li>
                  session cookies, which do not stay on your device after you
                  close your browser, and
                  <br />
                  <br />
                </li>
                <li>
                  persistent cookies, which remain on your device until you
                  delete them or they expire.
                </li>
              </ol>
            </li>
          </ol>
          <p>We may use the following categories of cookies on our Website.</p>
          <ol style={{ marginLeft: 60, listStyleType: "lower-roman" }}>
            <li>
              <strong>Strictly Necessary Cookies. </strong>These cookies are
              essential in order to enable you to move around the Website and
              use its features. Without these cookies, services you have
              requested, such as maintaining a record of your purchased items
              (e.g. a shopping cart), cannot be provided.
            </li>
            <li>
              <strong>Performance Cookies. </strong>These cookies collect
              anonymous information on how you use our Website to help us
              understand how you arrive at our Website, browse or use our
              Website and highlight areas where we can improve, such as
              navigation. The data stored by these cookies never shows personal
              details from which your individual identity can be established.
            </li>
            <li>
              <strong>Functionality Cookies.</strong> These cookies remember
              choices you make such as the country from which you visit our
              Website, your preferred language, and your search parameters. This
              information can then be used to provide you with an experience
              more appropriate to your selections and to make your visits to our
              Website more tailored to your preferences. The information in
              these cookies may be anonymized. These cookies cannot track your
              browsing activity on other websites.
            </li>
            <li>
              <strong>Targeting Cookies or Advertising Cookies.</strong>
              These cookies collect information about your browsing habits in
              order to make advertising more relevant to you and your interests.
              They are also used to limit the number of times you see an
              advertisement as well as help measure the effectiveness of an
              advertising campaign. The cookies are usually placed by
              third-party advertising networks. These cookies remember the
              websites you visit and that information is shared with other
              parties such as Google Display Network advertisers.
            </li>
          </ol>
          <p>
            Of course, if you do not wish to have cookies on your devices, you
            may turn them off at any time by modifying your internet
            browser&apos;s settings. However, by disabling cookies on your
            device, you may be prohibited from full use of the Website&apos;s
            features or lose access to some functionality.
          </p>
          <ol start={2} style={{ listStyleType: "lower-alpha" }}>
            <li>
              <u>Google Analytics</u>. The Website sends aggregated,
              non-Personal Information to Google Analytics for the purpose of
              providing us with the ability to conduct technical and statistical
              analysis on the Website&apos;s performance. For more information
              on how Google Analytics supports the Website and uses information
              sent from the Website, please review Google&apos;s privacy policy
              available at{" "}
              <a
                data-fr-linked="true"
                href="https://policies.google.com/technologies/partner-sites"
              >
                https://policies.google.com/technologies/partner-sites
              </a>
              .<br />
              <br />
            </li>
            <li>
              <u>Web Beacons</u>. A Web Beacon is an electronic image. Web
              Beacons can track certain things from your computer and can report
              activity back to a web server allowing us to understand some of
              your behavior. If you choose to receive emails from us, we may use
              Web Beacons to track your reaction to our emails. We may also use
              them to track if you click on the links and at what time and date
              you do so. Some of the third-party marketers we engage with may
              use Web Beacons to track your interaction with online advertising
              banners on our Website. This information is only collected in
              aggregate form and will not be linked to your Personal
              Information. Please note that any image file on a webpage can act
              as a Web Beacon.
              <br />
              <br />
            </li>
            <li>
              <u>Embedded Web Links</u>. Links provided in our emails and, in
              some cases, on third-party websites may include tracking
              technology embedded in the link. The tracking is accomplished
              through a redirection system. The redirection system allows us to
              understand how the link is being used by email recipients. Some of
              these links will enable us to identify that you have personally
              clicked on the link and this may be attached to the Personal
              Information that we hold about you. This data is used to improve
              our service to you and to help us understand the performance of
              our marketing campaigns.
              <br />
              <br />
            </li>
            <li>
              <u>Third-party Websites and Services</u>. We work with a number of
              service providers of marketing communications technology. These
              service providers may use various data collection methods to
              improve the performance of the marketing campaigns we are
              contracting them to provide. The information collected can be
              gathered on our Website and also on the websites where our
              marketing communications are appearing. For example, we may
              collect data where our banner advertisements are displayed on
              third-party websites.
              <br />
              <br />
              We do not control these third parties&apos; tracking technologies
              or how they may be used. If you have any questions about an
              advertisement, you should contact the responsible provider
              directly. For information about how you can opt out of receiving
              targeted advertising from many providers, see our{" "}
              <a href={privacyPath}>Privacy Policy</a> under the “Your Choices
              And Selecting Your Privacy Preferences” heading.
            </li>
          </ol>
        </div>
        <style jsx>{`
          p {
            margin-top: 12px;
          }
        `}</style>
      </Container>
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return { props: {} };
};

export default Page;
